import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowForward, Explore, FitnessCenter, Place } from '@mui/icons-material';
import type { CreateCommunityDiveSiteRequest, DiveSite } from '../types/DiveSite';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { authService } from '../services/authService';

const SITE_TYPE_LABELS: Record<DiveSite['siteType'], string> = {
  destination: 'Destination',
  reef: 'Reef',
  wreck: 'Wreck',
  'blue-hole': 'Blue hole',
  'cold-water': 'Cold water',
  drift: 'Drift',
  lake: 'Lake',
  quarry: 'Quarry',
  river: 'River',
  spring: 'Spring',
};

const SITE_TYPE_OPTIONS = Object.entries(SITE_TYPE_LABELS) as [DiveSite['siteType'], string][];

const DIFFICULTY_COLORS: Record<DiveSite['difficulty'], 'success' | 'warning' | 'error'> = {
  Beginner: 'success',
  Intermediate: 'warning',
  Advanced: 'error',
};

const normalize = (s: string) => s.toLowerCase().trim();

const scoreMatch = (site: DiveSite, query: string): number => {
  const q = normalize(query);
  if (!q) return 1;
  const name = normalize(site.name);
  if (name === q) return 4;
  if (name.startsWith(q)) return 3;
  if (name.includes(q)) return 2;
  const haystack = [site.region, site.country, ...(site.aliases ?? []), ...site.tags]
    .map(normalize).join(' ');
  if (haystack.includes(q)) return 1;
  return 0;
};

interface Props {
  siteName: string;
  linkedSiteSlug: string | null;
  onSiteNameChange: (name: string) => void;
  onSiteLinked: (site: DiveSite | null) => void;
}

const emptyForm = {
  name: '',
  region: '',
  country: '',
  latitude: '',
  longitude: '',
  matchRadiusKm: '25',
  siteType: 'destination' as DiveSite['siteType'],
  difficulty: 'Intermediate' as DiveSite['difficulty'],
  summary: '',
  highlights: '',
  tags: '',
};

const DiveSitePickerField: React.FC<Props> = ({ siteName, linkedSiteSlug, onSiteNameChange, onSiteLinked }) => {
  const navigate = useNavigate();
  const { allSites, loading: catalogLoading, addCommunitySite, saving: catalogSaving } = useDiveSiteCatalog();
  const currentUser = authService.getUser();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(siteName);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep internal input in sync when parent resets the form
  useEffect(() => { setInputValue(siteName); }, [siteName]);

  const linkedSite = useMemo(
    () => linkedSiteSlug ? allSites.find((s) => s.slug === linkedSiteSlug) ?? null : null,
    [allSites, linkedSiteSlug],
  );

  const suggestions = useMemo(() => {
    if (!inputValue.trim() || linkedSite) return [];
    return allSites
      .map((site) => ({ site, score: scoreMatch(site, inputValue) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(({ site }) => site);
  }, [allSites, inputValue, linkedSite]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    onSiteNameChange(value);
    if (linkedSite) onSiteLinked(null); // unlink if user edits after selecting
    setOpen(true);
  };

  const handleSelect = (site: DiveSite) => {
    setInputValue(site.name);
    onSiteNameChange(site.name);
    onSiteLinked(site);
    setOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    onSiteNameChange('');
    onSiteLinked(null);
    inputRef.current?.focus();
  };

  // --- inline site creation ---
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [createError, setCreateError] = useState<string | null>(null);

  const openCreate = () => {
    setForm({ ...emptyForm, name: inputValue });
    setCreateError(null);
    setCreateOpen(true);
    setOpen(false);
  };

  const handleCreate = async () => {
    setCreateError(null);
    if (!form.name.trim()) { setCreateError('Site name is required.'); return; }
    if (!form.region.trim()) { setCreateError('Region is required.'); return; }
    if (!form.country.trim()) { setCreateError('Country is required.'); return; }
    if (!form.summary.trim()) { setCreateError('Summary is required.'); return; }
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || lat < -90 || lat > 90) { setCreateError('Valid latitude (-90 to 90) is required.'); return; }
    if (isNaN(lng) || lng < -180 || lng > 180) { setCreateError('Valid longitude (-180 to 180) is required.'); return; }

    const parseList = (s: string) => s.split(',').map((v) => v.trim()).filter(Boolean);

    const request: CreateCommunityDiveSiteRequest = {
      name: form.name.trim(),
      region: form.region.trim(),
      country: form.country.trim(),
      latitude: lat,
      longitude: lng,
      matchRadiusKm: parseFloat(form.matchRadiusKm) || 25,
      siteType: form.siteType,
      difficulty: form.difficulty,
      summary: form.summary.trim(),
      highlights: parseList(form.highlights),
      tags: parseList(form.tags),
    };

    try {
      const created = await addCommunitySite(request);
      setInputValue(created.name);
      onSiteNameChange(created.name);
      onSiteLinked(created);
      setCreateOpen(false);
    } catch (err: any) {
      setCreateError(err?.message ?? 'Failed to create dive site.');
    }
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box position="relative">
          <TextField
            inputRef={inputRef}
            label="Dive Site"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => { if (!linkedSite && inputValue) setOpen(true); }}
            placeholder="Search or type a site name…"
            inputProps={{ maxLength: 200 }}
            fullWidth
            InputProps={catalogLoading ? {
              endAdornment: <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>,
            } : undefined}
          />

          {/* Dropdown */}
          {open && (suggestions.length > 0 || inputValue.trim()) && (
            <Paper
              elevation={8}
              sx={{ position: 'absolute', zIndex: 1400, width: '100%', mt: 0.5, maxHeight: 320, overflowY: 'auto' }}
            >
              {suggestions.map((site) => (
                <Box
                  key={site.slug}
                  onClick={() => handleSelect(site)}
                  sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{site.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {site.region}{site.country ? `, ${site.country}` : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} flexShrink={0} ml={1}>
                      <Chip label={SITE_TYPE_LABELS[site.siteType]} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                      <Chip label={site.difficulty} size="small" color={DIFFICULTY_COLORS[site.difficulty]} sx={{ fontSize: '0.65rem', height: 18 }} />
                    </Stack>
                  </Stack>
                </Box>
              ))}

              {suggestions.length > 0 && <Divider />}

              {currentUser && (
                <Box
                  onClick={openCreate}
                  sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Add fontSize="small" color="primary" />
                  <Typography variant="body2" color="primary.main" fontWeight={600}>
                    Add "{inputValue.trim() || 'new site'}" to the catalog
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      {/* Linked site info card */}
      <Collapse in={!!linkedSite}>
        {linkedSite && (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, borderColor: 'primary.main', bgcolor: 'primary.50' }}>
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Place fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>{linkedSite.name}</Typography>
                </Stack>
                <Button size="small" sx={{ minWidth: 0, p: 0.25, fontSize: '0.7rem' }} onClick={handleClear}>
                  Unlink
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {linkedSite.region}{linkedSite.country ? `, ${linkedSite.country}` : ''}
              </Typography>

              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                <Chip icon={<Explore sx={{ fontSize: '0.75rem !important' }} />} label={SITE_TYPE_LABELS[linkedSite.siteType]} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                <Chip icon={<FitnessCenter sx={{ fontSize: '0.75rem !important' }} />} label={linkedSite.difficulty} size="small" color={DIFFICULTY_COLORS[linkedSite.difficulty]} sx={{ fontSize: '0.7rem', height: 20 }} />
              </Stack>

              {linkedSite.summary && (
                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {linkedSite.summary}
                </Typography>
              )}

              {linkedSite.highlights.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ✦ {linkedSite.highlights.slice(0, 3).join(' · ')}
                </Typography>
              )}

              <Button
                size="small"
                endIcon={<ArrowForward fontSize="small" />}
                onClick={() => navigate(`/dive-sites/${linkedSite.slug}`)}
                sx={{ alignSelf: 'flex-start', p: 0, fontSize: '0.7rem' }}
              >
                View full site details
              </Button>
            </Stack>
          </Paper>
        )}
      </Collapse>

      {/* Create site dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Dive Site to Catalog</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}
            <TextField label="Site name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth required />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Region" value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} fullWidth required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Country" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} fullWidth required />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField label="Latitude" type="number" value={form.latitude} onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))} fullWidth required />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Longitude" type="number" value={form.longitude} onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))} fullWidth required />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Match radius (km)" type="number" value={form.matchRadiusKm} onChange={(e) => setForm((f) => ({ ...f, matchRadiusKm: e.target.value }))} fullWidth />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select label="Site type" value={form.siteType} onChange={(e) => setForm((f) => ({ ...f, siteType: e.target.value as DiveSite['siteType'] }))} fullWidth>
                  {SITE_TYPE_OPTIONS.map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Difficulty" value={form.difficulty} onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value as DiveSite['difficulty'] }))} fullWidth>
                  <MenuItem value="Beginner">Beginner</MenuItem>
                  <MenuItem value="Intermediate">Intermediate</MenuItem>
                  <MenuItem value="Advanced">Advanced</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <TextField label="Summary" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} multiline minRows={3} fullWidth required />
            <TextField label="Highlights" value={form.highlights} onChange={(e) => setForm((f) => ({ ...f, highlights: e.target.value }))} helperText="Comma-separated" fullWidth />
            <TextField label="Tags" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} helperText="Comma-separated" fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={catalogSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={catalogSaving}>
            {catalogSaving ? 'Adding…' : 'Add to catalog & select'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DiveSitePickerField;
